import { useCallback, useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import type { Session } from "@supabase/supabase-js";
import { supabase } from "../lib/supabase";
import { callSyncStatus } from "../lib/api";
import { useToast } from "../components/Toast";
import { StageTimeline, type MarcarPayload } from "../components/StageTimeline";
import { BotaoLinkAssinatura } from "../components/BotaoLinkAssinatura";
import { badgeContrato, derivarStageExibicao, mapStatusContratoBadge, type StageDef } from "../lib/stages";
import type { BoardRow, ContratoResumo, OnboardingPipeline } from "../lib/types";
import styles from "./ClienteDetalhe.module.css";

function fmtData(ts: string | null): string | null {
  if (!ts) return null;
  const d = new Date(ts);
  return Number.isNaN(d.getTime()) ? null : d.toLocaleDateString("pt-BR");
}

export function ClienteDetalhe({ session }: { session: Session }) {
  const { id } = useParams<{ id: string }>();
  const { show } = useToast();
  const email = session.user.email ?? "";

  const [row, setRow] = useState<BoardRow | null>(null);
  const [pipeline, setPipeline] = useState<OnboardingPipeline | null>(null);
  const [contratos, setContratos] = useState<ContratoResumo[]>([]);
  const [loading, setLoading] = useState(true);
  const [salvando, setSalvando] = useState<number | null>(null);
  const [sincronizando, setSincronizando] = useState(false);
  const [confirmando, setConfirmando] = useState(false);

  const carregar = useCallback(async () => {
    if (!id) return;
    const [b, p] = await Promise.all([
      supabase.from("v_onboarding_board").select("*").eq("cliente_id", id).single(),
      supabase.from("onboarding_pipeline").select("*").eq("cliente_id", id).single(),
    ]);
    if (b.error) show("error", `Falha ao carregar cliente: ${b.error.message}`);
    const boardRow = (b.data as BoardRow) ?? null;
    setRow(boardRow);
    setPipeline((p.data as OnboardingPipeline) ?? null);

    // Todos os contratos do cliente lógico (todas as linhas do grupo).
    if (boardRow?.cliente_ids?.length) {
      const { data: cs } = await supabase
        .from("v_cliente_contratos")
        .select("*")
        .in("cliente_id", boardRow.cliente_ids)
        .order("rank", { ascending: false })
        .order("created_at", { ascending: false });
      setContratos((cs as ContratoResumo[]) ?? []);
    } else {
      setContratos([]);
    }
    setLoading(false);
  }, [id, show]);

  useEffect(() => {
    carregar();
  }, [carregar]);

  async function onSincronizar() {
    setSincronizando(true);
    const r = await callSyncStatus();
    if (r.ok) {
      show("success", "Sincronização disparada.");
      setTimeout(carregar, 4000);
    } else {
      show("error", `Falha ao sincronizar: ${r.message ?? "erro"}`);
    }
    setSincronizando(false);
  }

  async function onConfirmarContrato(confirmar: boolean) {
    if (!id) return;
    setConfirmando(true);
    const agora = new Date().toISOString();
    const patch: Record<string, unknown> = {
      cliente_id: id,
      stage: Math.max(pipeline?.stage ?? 0, 1),
      atualizado_em: agora,
      contrato_confirmado_em: confirmar ? agora : null,
      contrato_confirmado_por: confirmar ? email : null,
    };
    const { error } = await supabase.from("onboarding_pipeline").upsert(patch, { onConflict: "cliente_id" });
    if (error) {
      setConfirmando(false);
      show("error", `Falha ao confirmar: ${error.message}`);
      return;
    }
    await supabase.from("onboarding_stage_history").insert({
      cliente_id: id,
      de_stage: row?.stage ?? null,
      para_stage: row?.stage ?? null,
      por: email,
      nota: confirmar ? "Contrato confirmado manualmente" : "Confirmação manual desfeita",
    });
    setConfirmando(false);
    show("success", confirmar ? "Contrato confirmado." : "Confirmação desfeita.");
    carregar();
  }

  async function onMarcar(stage: StageDef, payload: MarcarPayload) {
    if (!id || !row) return;
    setSalvando(stage.n);

    const deStage = derivarStageExibicao(row);
    const tsValue = payload.data ? new Date(`${payload.data}T12:00:00`).toISOString() : new Date().toISOString();
    const paraStage = Math.max(stage.n, pipeline?.stage ?? 0);

    const patch: Record<string, unknown> = {
      cliente_id: id,
      [stage.campo]: tsValue,
      stage: paraStage,
      atualizado_por: email,
      atualizado_em: new Date().toISOString(),
    };
    if (payload.link !== undefined && payload.link !== "") patch.reuniao_link = payload.link;
    if (payload.grupo_id !== undefined && payload.grupo_id !== "") patch.grupo_id = payload.grupo_id;

    // upsert (não update): cria a linha do pipeline se ela ainda não existir
    // (ex.: cliente vindo do n8n sem linha) — senão o update afetaria 0 linhas.
    const { error } = await supabase.from("onboarding_pipeline").upsert(patch, { onConflict: "cliente_id" });
    if (error) {
      setSalvando(null);
      show("error", `Falha ao atualizar etapa: ${error.message}`);
      return;
    }

    await supabase.from("onboarding_stage_history").insert({
      cliente_id: id,
      de_stage: deStage,
      para_stage: paraStage,
      por: email,
      nota: payload.nota ?? null,
    });

    // Etapa 5 segue manual; gancho n8n para envio de msg quando existir.
    if (stage.n === 5) {
      // TODO: webhook n8n quando existir — enviar mensagem de onboarding.
    }

    setSalvando(null);
    show("success", `Etapa "${stage.label}" concluída.`);
    carregar();
  }

  if (loading) return <div className="splash">Carregando cliente…</div>;
  if (!row) return (
    <div className="splash">
      <div style={{ textAlign: "center" }}>
        <p className="muted">Cliente não encontrado.</p>
        <Link to="/" className="btn btn-ghost" style={{ marginTop: "var(--s-3)" }}>Voltar ao board</Link>
      </div>
    </div>
  );

  const badge = badgeContrato(row.contrato_status, row.contrato_confirmado_em);
  const stageExibicao = derivarStageExibicao(row);
  const confirmado = !!row.contrato_confirmado_em;

  return (
    <div>
      <Link to="/" className={styles.voltar}>← Board</Link>

      <header className={styles.head}>
        <div>
          <h1>{row.nome_projeto ?? row.cliente_nome ?? "Sem nome"}</h1>
          {row.nome_projeto && row.cliente_nome && row.nome_projeto !== row.cliente_nome && (
            <div className="dim" style={{ fontSize: 13, marginTop: 2 }}>{row.cliente_nome}</div>
          )}
          <div className={styles.meta}>
            <span className={`badge badge-${badge.tone}`}>{badge.label}</span>
            {row.modelo_contrato && <span className="dim mono">{row.modelo_contrato}</span>}
            {row.valor_mensal_numero != null && <span className="muted">R$ {Number(row.valor_mensal_numero).toLocaleString("pt-BR")}/mês</span>}
            {row.vigencia_meses != null && <span className="muted">{row.vigencia_meses} meses</span>}
            <span className="dim">Etapa {stageExibicao}/9</span>
          </div>
        </div>
        <div style={{ display: "flex", gap: "var(--s-2)", flexWrap: "wrap" }}>
          {row.d4sign_document_uuid && (
            <BotaoLinkAssinatura doc={row.d4sign_document_uuid} clientNome={row.cliente_nome} />
          )}
          <button className="btn btn-ghost" onClick={onSincronizar} disabled={sincronizando}>
            {sincronizando ? "Sincronizando…" : "Sincronizar status"}
          </button>
        </div>
      </header>

      {row.observacoes && (
        <p className={styles.obs}>{row.observacoes}</p>
      )}

      {/* ------------------------------------------------------------------ */}
      {/* Briefing de fechamento — gerado pelo n8n a partir da transcrição.   */}
      {/* ------------------------------------------------------------------ */}
      {row.briefing_resumo && (
        <section className="card" style={{ marginBottom: "var(--s-4)" }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", gap: "var(--s-2)", flexWrap: "wrap" }}>
            <h3 style={{ margin: 0 }}>Briefing de fechamento</h3>
            <span className="dim" style={{ fontSize: 12 }}>
              {row.briefing_gerado_em ? new Date(row.briefing_gerado_em).toLocaleDateString("pt-BR") : ""}
              {row.clickup_projeto_url && (
                <>
                  {" · "}
                  <a href={row.clickup_projeto_url} target="_blank" rel="noreferrer">abrir no ClickUp</a>
                </>
              )}
            </span>
          </div>
          <pre style={{ whiteSpace: "pre-wrap", fontFamily: "inherit", fontSize: 14, margin: "var(--s-3) 0 0", lineHeight: 1.55 }}>
            {row.briefing_resumo}
          </pre>
        </section>
      )}

      {/* ------------------------------------------------------------------ */}
      {/* Contratos do cliente — todos os contratos do mesmo CPF/CNPJ.        */}
      {/* ------------------------------------------------------------------ */}
      <section className={`card ${styles.contratosCard}`}>
        <div className={styles.contratosHead}>
          <h3>Contratos do cliente</h3>
          {confirmado ? (
            <div className={styles.confirmInfo}>
              <span className="badge badge-success">Confirmado manualmente</span>
              <button className="btn btn-ghost" onClick={() => onConfirmarContrato(false)} disabled={confirmando}>
                {confirmando ? "…" : "Desfazer"}
              </button>
            </div>
          ) : (
            <button className="btn btn-primary" onClick={() => onConfirmarContrato(true)} disabled={confirmando}>
              {confirmando ? "Confirmando…" : "Confirmar contrato (manual)"}
            </button>
          )}
        </div>
        {confirmado && (
          <p className="dim" style={{ fontSize: 12, marginBottom: "var(--s-3)" }}>
            Confirmado por {row.contrato_confirmado_por ?? "—"} em {fmtData(row.contrato_confirmado_em)}.
          </p>
        )}

        {contratos.length === 0 ? (
          <p className="dim" style={{ fontSize: 13 }}>Nenhum contrato gerado para este cliente.</p>
        ) : (
          <ul className={styles.contratosLista}>
            {contratos.map((c, i) => {
              const cb = mapStatusContratoBadge(c.status);
              const principal = i === 0;
              return (
                <li key={c.contrato_id} className={`${styles.contratoItem} ${principal ? styles.principal : ""}`}>
                  <div className={styles.contratoTop}>
                    <span className={`badge badge-${cb.tone}`}>{cb.label}</span>
                    {principal && contratos.length > 1 && <span className="badge badge-info">mais avançado</span>}
                    {c.modelo_contrato && <span className="dim mono">{c.modelo_contrato}</span>}
                    {c.valor_mensal_numero != null && (
                      <span className="muted">R$ {Number(c.valor_mensal_numero).toLocaleString("pt-BR")}/mês</span>
                    )}
                  </div>
                  <div className={styles.contratoMeta}>
                    {fmtData(c.created_at) && <span className="dim">criado {fmtData(c.created_at)}</span>}
                    {fmtData(c.data_ativacao) && <span className="dim">· ativado {fmtData(c.data_ativacao)}</span>}
                    {c.vigencia_meses != null && <span className="dim">· {c.vigencia_meses} meses</span>}
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </section>

      <div className={`card ${styles.timelineCard}`}>
        <StageTimeline
          row={row}
          pipeline={pipeline}
          stageExibicao={stageExibicao}
          salvando={salvando}
          sincronizando={sincronizando}
          onMarcar={onMarcar}
          onSincronizar={onSincronizar}
        />
      </div>
    </div>
  );
}
